import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

export type AdminRole = 'EMPLOYEE' | 'ADMIN'

export interface AdminUserPosition {
  id: string
  name: string
  sortOrder: number
}

export interface AdminUser {
  id: string
  email: string
  name: string
  role: AdminRole
  active: boolean
  passwordChangedAt: string | null
  rodoSeenAt: string | null
  positionId: string | null
  position: AdminUserPosition | null
  employmentFraction: number | null
  invitedAt: string | null
  hasPassword: boolean
  deletedAt: string | null
  hasChildrenUnder14: boolean
  hourlyRate: number | null
  createdAt: string
}

export interface InvitationResponse {
  user: AdminUser
  invitationLink: string
  expiresAt: string
}

export type AdminUsersFilter = {
  active?: 'true' | 'false' | 'all'
  role?: AdminRole | 'all'
  includeDeleted?: boolean
}

const ADMIN_USERS_KEY = ['admin-users'] as const

export function useAdminUsers(filters?: AdminUsersFilter) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'list', filters ?? null],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (filters?.active) qs.set('active', filters.active)
      if (filters?.role) qs.set('role', filters.role)
      if (filters?.includeDeleted) qs.set('includeDeleted', 'true')
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return apiFetch<{ users: AdminUser[] }>(`/api/admin/users${suffix}`)
    },
    staleTime: 30_000,
  })
}

export function useAdminUser(id: string | undefined) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'one', id],
    queryFn: () => apiFetch<{ user: AdminUser }>(`/api/admin/users/${id}`),
    enabled: Boolean(id),
  })
}

export interface CreateUserBody {
  email: string
  name: string
  role: AdminRole
  positionId?: string | null
  employmentFraction?: number | null
  hasChildrenUnder14?: boolean
  hourlyRate?: number | null
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateUserBody) =>
      apiFetch<InvitationResponse>('/api/admin/users', {
        method: 'POST',
        body: { ...body },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  })
}

export function useResendInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<InvitationResponse>(
        `/api/admin/users/${userId}/resend-invitation`,
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  })
}

export interface UpdateUserBody {
  id: string
  name?: string
  role?: AdminRole
  active?: boolean
  positionId?: string | null
  employmentFraction?: number | null
  hasChildrenUnder14?: boolean
  hourlyRate?: number | null
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateUserBody) =>
      apiFetch<{ user: AdminUser }>(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body: { ...patch },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  })
}

export function useResetUserPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ user: AdminUser; temporaryPassword: string }>(
        `/api/admin/users/${id}/reset-password`,
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  })
}

export function useAnonymizeUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ user: AdminUser }>(`/api/admin/users/${userId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  })
}
